import { Crosshair, Shield, Radio, Plane, Ship, TrendingUp, AlertTriangle, CloudRain, Flame, Wifi, Factory, Target } from 'lucide-react'

export interface LayerState {
  cyber: boolean
  conflict: boolean
  infrastructure: boolean
  outages: boolean
  natural: boolean
  weather: boolean
  flights: boolean
  maritime: boolean
  economic: boolean
  sanctions: boolean
  gamma?: boolean
  bases?: boolean
  /** Not shown in sidebar; kept for compatibility. */
  telegram?: boolean
  /** Not shown in sidebar; kept for compatibility. */
  gpsjam?: boolean
}

const LAYERS: { key: keyof LayerState; label: string; icon: any; activeColor: string; domainFilter: string }[] = [
  { key: 'cyber', label: 'Cyber', icon: Shield, activeColor: 'text-purple-400 border-purple-500/50 bg-purple-500/10', domainFilter: 'cyber' },
  { key: 'conflict', label: 'Conflict', icon: Crosshair, activeColor: 'text-red-400 border-red-500/50 bg-red-500/10', domainFilter: 'geopolitical' },
  { key: 'infrastructure', label: 'Infra', icon: Radio, activeColor: 'text-amber-400 border-amber-500/50 bg-amber-500/10', domainFilter: 'infrastructure' },
  { key: 'outages', label: 'Outages', icon: Wifi, activeColor: 'text-rose-400 border-rose-500/50 bg-rose-500/10', domainFilter: 'infrastructure' },
  { key: 'natural', label: 'Natural', icon: Flame, activeColor: 'text-orange-400 border-orange-500/50 bg-orange-500/10', domainFilter: 'geopolitical' },
  { key: 'weather', label: 'Weather', icon: CloudRain, activeColor: 'text-sky-400 border-sky-500/50 bg-sky-500/10', domainFilter: 'geopolitical' },
  { key: 'flights', label: 'Flights', icon: Plane, activeColor: 'text-green-400 border-green-500/50 bg-green-500/10', domainFilter: '' },
  { key: 'maritime', label: 'Maritime', icon: Ship, activeColor: 'text-blue-400 border-blue-500/50 bg-blue-500/10', domainFilter: '' },
  { key: 'economic', label: 'Economic', icon: TrendingUp, activeColor: 'text-emerald-400 border-emerald-500/50 bg-emerald-500/10', domainFilter: '' },
  { key: 'sanctions', label: 'Sanctions', icon: AlertTriangle, activeColor: 'text-orange-400 border-orange-500/50 bg-orange-500/10', domainFilter: '' },
  { key: 'gamma', label: 'Gamma', icon: Factory, activeColor: 'text-yellow-300 border-yellow-400/60 bg-yellow-500/10', domainFilter: '' },
  { key: 'bases', label: 'Bases', icon: Target, activeColor: 'text-rose-300 border-rose-400/60 bg-rose-500/10', domainFilter: '' },
]

interface LayerTogglesProps {
  layers: LayerState
  onChange: (layers: LayerState) => void
}

export function getDomainsFromLayers(layers: LayerState): string | undefined {
  const active: string[] = []
  if (layers.cyber) active.push('cyber')
  if (layers.conflict || layers.natural || layers.weather) active.push('geopolitical')
  if (layers.infrastructure || layers.outages || layers.economic || layers.flights || layers.maritime) active.push('infrastructure')
  return active.length > 0 && active.length < 4 ? active.join(',') : undefined
}

export function getEventTypesFromLayers(layers: LayerState): string | undefined {
  const types: string[] = []
  if (layers.cyber) types.push('cyber_attack', 'vulnerability', 'ransomware')
  if (layers.conflict) types.push('conflict', 'explosion', 'protest', 'strategic_development')
  if (layers.natural || layers.weather) types.push('disaster')
  if (layers.infrastructure || layers.outages) types.push('infrastructure_outage')
  if (layers.flights) types.push('flight')
  if (layers.maritime) types.push('maritime')
  if (layers.economic) types.push('economic')
  return types.length > 0 ? types.join(',') : undefined
}

export const DEFAULT_LAYERS: LayerState = {
  cyber: true,
  conflict: true,
  infrastructure: true,
  outages: false,
  natural: true,
  weather: false,
  flights: false,
  maritime: false,
  economic: false,
  sanctions: false,
  telegram: false,
  gpsjam: false,
  gamma: false,
  bases: false,
}

export default function LayerToggles({ layers, onChange }: LayerTogglesProps) {
  const toggle = (key: keyof LayerState) => {
    onChange({ ...layers, [key]: !layers[key] })
  }

  const activeCount = Object.values(layers).filter(Boolean).length

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded px-3 py-2.5 shadow-[0_0_15px_rgba(34,197,94,0.06)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] text-gray-500 uppercase tracking-[0.15em] font-mono">Layers</span>
        <span className="text-[9px] text-green-400/80 font-mono drop-shadow-[0_0_4px_rgba(74,222,128,0.3)]">{activeCount} active</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5 max-h-[220px] overflow-y-auto">
        {LAYERS.map(({ key, label, icon: Icon, activeColor }) => {
          const active = layers[key]
          return (
            <button
              key={key}
              onClick={() => toggle(key)}
              className={`flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded border text-[9px] font-mono transition-all ${
                active
                  ? activeColor
                  : 'bg-gray-900/50 text-gray-700 border-gray-800/50 hover:border-gray-700/50 hover:text-gray-500'
              }`}
              title={label}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="truncate w-full text-center">{label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
